import type {
  ItemAiLookupFieldUpdate,
  ItemAiLookupReviewItem,
  ItemAiLookupRun,
} from '@/data/itemAiLookup';

export type ReviewGroup = 'details' | 'facets' | 'gear' | 'media';

type ReviewChoiceBase = {
  id: string;
  group: ReviewGroup;
  label: string;
  operation: 'Add' | 'Replace' | 'Update';
  proposalKey: string;
};

export type ReviewFieldChoice = ReviewChoiceBase & {
  kind: 'field';
  fieldKey: string;
  current: unknown;
  proposed: unknown;
  inputType: 'text' | 'number' | 'date';
  longText: boolean;
};

export type ReviewAttributeChoice = ReviewChoiceBase & {
  kind: 'attribute';
  current: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
};

export type ReviewAccessoryChoice = ReviewChoiceBase & {
  kind: 'accessory';
  current: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
};

export type ReviewCaseChoice = ReviewChoiceBase & {
  kind: 'case';
  current: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
};

export type ReviewDocumentChoice = ReviewChoiceBase & {
  kind: 'document';
  current: null;
  proposed: Record<string, unknown>;
  importAsManual: boolean;
};

export type ReviewImageChoice = ReviewChoiceBase & {
  kind: 'image';
  current: string | null;
  proposed: Record<string, unknown>;
};

export type ReviewChoice =
  | ReviewFieldChoice
  | ReviewAttributeChoice
  | ReviewAccessoryChoice
  | ReviewCaseChoice
  | ReviewDocumentChoice
  | ReviewImageChoice;

export type ReviewModel = {
  patch: Record<string, unknown>;
  choices: ReviewChoice[];
  unchangedCount: number;
};

export type ReviewApplyDecisions = {
  updates: ItemAiLookupFieldUpdate[];
  rejectedKeys: string[];
};

const FIELD_CONFIG: Record<string, {
  label: string;
  group: ReviewGroup;
  inputType?: 'text' | 'number' | 'date';
  longText?: boolean;
}> = {
  external_description: { label: 'Primary item description', group: 'details', longText: true },
  brand: { label: 'Brand', group: 'details' },
  manufacturer_name: { label: 'Manufacturer', group: 'details' },
  model_number: { label: 'Model number', group: 'details' },
  model_name: { label: 'Model name', group: 'details' },
  mpn: { label: 'Manufacturer part number', group: 'details' },
  length: { label: 'Length (in)', group: 'details', inputType: 'number' },
  width: { label: 'Width (in)', group: 'details', inputType: 'number' },
  height: { label: 'Height (in)', group: 'details', inputType: 'number' },
  weight: { label: 'Weight (lb)', group: 'details', inputType: 'number' },
  firmware_latest_version: { label: 'Latest firmware', group: 'media' },
  firmware_download_url: { label: 'Firmware download', group: 'media' },
  firmware_release_date: { label: 'Firmware release date', group: 'media', inputType: 'date' },
  firmware_notes: { label: 'Firmware notes', group: 'media', longText: true },
};

const CANONICAL_FACET_FIELDS: Array<{ key: string; label: string; type: 'set' | 'string' }> = [
  { key: 'signal_inputs', label: 'Signal inputs', type: 'set' },
  { key: 'signal_outputs', label: 'Signal outputs', type: 'set' },
  { key: 'signal_bidirectional', label: 'Bidirectional I/O', type: 'set' },
  { key: 'power_requirements', label: 'Power requirements', type: 'set' },
  { key: 'control_protocols', label: 'Control protocols', type: 'set' },
  { key: 'mounting_notes', label: 'Mounting notes', type: 'string' },
  { key: 'rigging_notes', label: 'Rigging notes', type: 'string' },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function cleanText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function normaliseText(value: unknown): string {
  return cleanText(value)
    .toLocaleLowerCase()
    .replace(/[‐‑‒–—]/g, '-')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ');
}

function numericValue(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!/^[-+]?\d+(?:\.\d+)?(?:\s*(?:in|inch(?:es)?|lb|lbs|pounds?))?$/i.test(text)) return null;
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function canonicalValue(value: unknown): unknown {
  if (value == null || value === '') return null;
  const numeric = numericValue(value);
  if (numeric != null) return Math.round(numeric * 10000) / 10000;
  if (Array.isArray(value)) {
    return value
      .map(canonicalValue)
      .filter((entry) => entry != null)
      .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  }
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .filter((key) => value[key] != null && value[key] !== '')
        .map((key) => [key, canonicalValue(value[key])]),
    );
  }
  return normaliseText(value);
}

export function reviewValuesMatch(left: unknown, right: unknown): boolean {
  return JSON.stringify(canonicalValue(left)) === JSON.stringify(canonicalValue(right));
}

function firmwarePatchValue(patch: Record<string, unknown>, key: string): unknown {
  const firmware = isRecord(patch.firmware) ? patch.firmware : {};
  if (key === 'firmware_latest_version') return firmware.latest_version;
  if (key === 'firmware_download_url') return firmware.download_url;
  if (key === 'firmware_release_date') return firmware.release_date;
  if (key === 'firmware_notes') return firmware.notes;
  return undefined;
}

function effectiveProposal(run: ItemAiLookupRun): Record<string, unknown> {
  const result = isRecord(run.result_json) ? run.result_json : {};
  const structured = isRecord(result.structured) ? result.structured : result;
  const proposed = isRecord(run.proposed_update) ? run.proposed_update : {};
  const base = Object.keys(proposed).length > 0 ? proposed : structured;
  return { ...base };
}

function normaliseAttribute(entry: unknown): Record<string, unknown> | null {
  if (!isRecord(entry)) return null;
  const key = cleanText(entry.key || entry.name || entry.label);
  if (!key) return null;
  const values = Array.isArray(entry.values)
    ? entry.values.filter((value) => value != null && cleanText(value))
    : [];
  const value = entry.value == null || entry.value === '' ? null : entry.value;
  if (value == null && values.length === 0) return null;
  return {
    key,
    type: cleanText(entry.type) || (values.length ? 'set' : typeof value),
    value,
    values,
    unit: cleanText(entry.unit) || null,
    notes: cleanText(entry.notes) || null,
  };
}

function proposalAttributes(patch: Record<string, unknown>): Record<string, unknown>[] {
  const attributes: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  const add = (entry: unknown) => {
    const attribute = normaliseAttribute(entry);
    if (!attribute) return;
    const key = normaliseText(attribute.key);
    if (!key || seen.has(key)) return;
    seen.add(key);
    attributes.push(attribute);
  };
  if (Array.isArray(patch.attributes)) patch.attributes.forEach(add);
  CANONICAL_FACET_FIELDS.forEach((config) => {
    if (seen.has(normaliseText(config.key))) return;
    const value = patch[config.key];
    if (config.type === 'set') {
      const values = Array.isArray(value)
        ? value.filter((entry) => cleanText(entry))
        : cleanText(value)
          ? [value]
          : [];
      if (values.length) add({ key: config.key, type: 'set', values });
      return;
    }
    if (cleanText(value)) add({ key: config.key, type: 'string', value });
  });
  return attributes;
}

function currentAttributes(item: ItemAiLookupReviewItem | null): Record<string, unknown>[] {
  if (!item) return [];
  if (Array.isArray(item.attributes)) {
    return item.attributes.map(normaliseAttribute).filter((entry): entry is Record<string, unknown> => !!entry);
  }
  if (isRecord(item.attributes)) {
    return Object.entries(item.attributes)
      .map(([key, value]) => normaliseAttribute(isRecord(value) ? { key, ...value } : { key, value }))
      .filter((entry): entry is Record<string, unknown> => !!entry);
  }
  return [];
}

function attributeDisplayName(key: unknown): string {
  const normalized = normaliseText(key).replace(/[\s-]+/g, '_');
  const canonical = CANONICAL_FACET_FIELDS.find((entry) => entry.key === normalized);
  if (canonical) return canonical.label;
  const acronyms = new Set(['ac', 'dsp', 'hdmi', 'io', 'ip', 'sdi', 'spl', 'usb', 'xlr']);
  return cleanText(key)
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((token) => acronyms.has(token.toLowerCase()) ? token.toUpperCase() : `${token.charAt(0).toUpperCase()}${token.slice(1)}`)
    .join(' ');
}

function normaliseAccessory(entry: unknown): Record<string, unknown> | null {
  if (!isRecord(entry)) return null;
  const name = cleanText(entry.name);
  if (!name) return null;
  const quantity = Math.max(1, Math.round(Number(entry.quantity) || 1));
  return {
    name,
    quantity,
    kind: cleanText(entry.kind) || null,
    notes: cleanText(entry.notes) || null,
    essential: entry.essential == null ? null : entry.essential === true,
  };
}

function normaliseCase(entry: unknown): Record<string, unknown> | null {
  if (!isRecord(entry)) return null;
  const name = cleanText(entry.name);
  const modelNumber = cleanText(entry.model_number);
  if (!name && !modelNumber) return null;
  return {
    name: name || modelNumber,
    manufacturer: cleanText(entry.manufacturer) || null,
    model_number: modelNumber || null,
    quantity: Math.max(1, Math.round(Number(entry.quantity) || 1)),
    notes: cleanText(entry.notes) || null,
  };
}

function normaliseDocument(entry: unknown): Record<string, unknown> | null {
  if (!isRecord(entry)) return null;
  const url = cleanText(entry.url);
  if (!url) return null;
  return {
    title: cleanText(entry.title) || url,
    url,
    kind: cleanText(entry.kind) || null,
    source_url: cleanText(entry.source_url) || null,
  };
}

function documentUrl(entry: unknown): string {
  if (typeof entry === 'string') return normaliseText(entry);
  return isRecord(entry) ? normaliseText(entry.url) : '';
}

function itemDocumentation(item: ItemAiLookupReviewItem | null): Array<Record<string, unknown> | string> {
  if (!item) return [];
  if (Array.isArray(item.documentation)) {
    return item.documentation.filter((entry): entry is Record<string, unknown> | string => (
      typeof entry === 'string' || isRecord(entry)
    ));
  }
  return [];
}

function caseIdentity(entry: Record<string, unknown>): string {
  const model = normaliseText(entry.model_number);
  if (model) return `model:${model}`;
  return `name:${normaliseText(entry.manufacturer)}:${normaliseText(entry.name)}`;
}

function choiceId(prefix: string, value: unknown, index: number): string {
  const normalized = normaliseText(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${prefix}:${normalized || index}`;
}

export function buildItemAiLookupReviewModel(
  run: ItemAiLookupRun,
  item: ItemAiLookupReviewItem | null,
): ReviewModel {
  const patch = effectiveProposal(run);
  const choices: ReviewChoice[] = [];
  let unchangedCount = 0;

  Object.entries(FIELD_CONFIG).forEach(([fieldKey, config]) => {
    const proposed = fieldKey.startsWith('firmware_') ? firmwarePatchValue(patch, fieldKey) : patch[fieldKey];
    if (proposed == null || (typeof proposed === 'string' && !proposed.trim())) return;
    const current = item ? item[fieldKey as keyof ItemAiLookupReviewItem] : null;
    if (reviewValuesMatch(current, proposed)) {
      unchangedCount += 1;
      return;
    }
    choices.push({
      id: `field:${fieldKey}`,
      kind: 'field',
      group: config.group,
      label: config.label,
      operation: current == null || current === '' ? 'Add' : 'Replace',
      proposalKey: fieldKey,
      fieldKey,
      current,
      proposed,
      inputType: config.inputType || 'text',
      longText: config.longText === true,
    });
  });

  const currentAttributeMap = new Map(
    currentAttributes(item).map((attribute) => [normaliseText(attribute.key), attribute]),
  );
  proposalAttributes(patch).forEach((attribute, index) => {
    const key = normaliseText(attribute.key);
    const current = currentAttributeMap.get(key) || null;
    if (current && reviewValuesMatch(current, attribute)) {
      unchangedCount += 1;
      return;
    }
    choices.push({
      id: choiceId('attribute', key, index),
      kind: 'attribute',
      group: 'facets',
      label: attributeDisplayName(attribute.key),
      operation: current ? 'Replace' : 'Add',
      proposalKey: 'attributes',
      current,
      proposed: attribute,
    });
  });

  const currentAccessoryMap = new Map(
    (item?.accessories || [])
      .map(normaliseAccessory)
      .filter((entry): entry is Record<string, unknown> => !!entry)
      .map((entry) => [normaliseText(entry.name), entry]),
  );
  const seenAccessories = new Set<string>();
  (Array.isArray(patch.accessories) ? patch.accessories : []).forEach((entry, index) => {
    const accessory = normaliseAccessory(entry);
    if (!accessory) return;
    const key = normaliseText(accessory.name);
    if (seenAccessories.has(key)) {
      unchangedCount += 1;
      return;
    }
    seenAccessories.add(key);
    const current = currentAccessoryMap.get(key) || null;
    if (current && reviewValuesMatch(current, accessory)) {
      unchangedCount += 1;
      return;
    }
    choices.push({
      id: choiceId('accessory', key, index),
      kind: 'accessory',
      group: 'gear',
      label: cleanText(accessory.name),
      operation: current ? 'Update' : 'Add',
      proposalKey: 'accessories',
      current,
      proposed: accessory,
    });
  });

  const currentCaseMap = new Map(
    (item?.cases || [])
      .map(normaliseCase)
      .filter((entry): entry is Record<string, unknown> => !!entry)
      .map((entry) => [caseIdentity(entry), entry]),
  );
  const seenCases = new Set<string>();
  (Array.isArray(patch.cases) ? patch.cases : []).forEach((entry, index) => {
    const caseSuggestion = normaliseCase(entry);
    if (!caseSuggestion) return;
    const key = caseIdentity(caseSuggestion);
    if (seenCases.has(key)) {
      unchangedCount += 1;
      return;
    }
    seenCases.add(key);
    const current = currentCaseMap.get(key) || null;
    if (current && reviewValuesMatch(current, caseSuggestion)) {
      unchangedCount += 1;
      return;
    }
    choices.push({
      id: choiceId('case', key, index),
      kind: 'case',
      group: 'gear',
      label: cleanText(caseSuggestion.name) || 'Protective case',
      operation: current ? 'Update' : 'Add',
      proposalKey: 'cases',
      current,
      proposed: caseSuggestion,
    });
  });

  const currentDocumentUrls = new Set(itemDocumentation(item).map(documentUrl).filter(Boolean));
  const manual = normaliseDocument(patch.manual);
  const manualUrl = documentUrl(manual);
  const proposedDocuments = [
    ...(Array.isArray(patch.documentation) ? patch.documentation : []),
    ...(manual ? [manual] : []),
  ];
  const seenDocumentUrls = new Set<string>();
  proposedDocuments.forEach((entry, index) => {
    const document = normaliseDocument(entry);
    if (!document) return;
    const url = documentUrl(document);
    if (!url || seenDocumentUrls.has(url)) {
      unchangedCount += 1;
      return;
    }
    seenDocumentUrls.add(url);
    if (currentDocumentUrls.has(url)) {
      unchangedCount += 1;
      return;
    }
    choices.push({
      id: choiceId('document', url, index),
      kind: 'document',
      group: 'media',
      label: cleanText(document.title) || 'Documentation',
      operation: 'Add',
      proposalKey: 'documentation',
      current: null,
      proposed: document,
      importAsManual: Boolean(manualUrl && url === manualUrl),
    });
  });

  const image: Record<string, unknown> | null = isRecord(patch.product_image) && cleanText(patch.product_image.url)
    ? { ...patch.product_image, url: cleanText(patch.product_image.url) }
    : null;
  if (image) {
    if (reviewValuesMatch(item?.ai_image_source_url, image.url)) {
      unchangedCount += 1;
    } else {
      choices.push({
        id: 'image:product',
        kind: 'image',
        group: 'media',
        label: cleanText(image.title) || 'Product image',
        operation: item?.ai_image_source_url ? 'Replace' : 'Add',
        proposalKey: 'product_image',
        current: item?.ai_image_source_url || null,
        proposed: image,
      });
    }
  }

  return { patch, choices, unchangedCount };
}

function mergeAttributes(
  current: ItemAiLookupReviewItem | null,
  selected: ReviewAttributeChoice[],
): Record<string, unknown>[] {
  const merged = currentAttributes(current);
  const indexes = new Map(merged.map((entry, index) => [normaliseText(entry.key), index]));
  selected.forEach((choice) => {
    const key = normaliseText(choice.proposed.key);
    const index = indexes.get(key);
    if (index == null) {
      indexes.set(key, merged.length);
      merged.push(choice.proposed);
    } else {
      merged[index] = choice.proposed;
    }
  });
  return merged;
}

function mergeDocumentation(
  current: ItemAiLookupReviewItem | null,
  selected: ReviewDocumentChoice[],
): Array<Record<string, unknown> | string> {
  const merged = [...itemDocumentation(current)];
  const urls = new Set(merged.map(documentUrl).filter(Boolean));
  selected.forEach((choice) => {
    const url = documentUrl(choice.proposed);
    if (!url || urls.has(url)) return;
    urls.add(url);
    merged.push(choice.proposed);
  });
  return merged;
}

function fieldDraftValue(choice: ReviewFieldChoice, draftValues: Record<string, string>): unknown {
  const raw = draftValues[choice.id] ?? cleanText(choice.proposed);
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (choice.inputType === 'number') {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : trimmed;
  }
  return trimmed;
}

export function buildItemAiLookupApplyDecisions(options: {
  choices: ReviewChoice[];
  selectedIds: Set<string>;
  draftValues: Record<string, string>;
  item: ItemAiLookupReviewItem | null;
}): ReviewApplyDecisions {
  const { choices, selectedIds, draftValues, item } = options;
  const selected = choices.filter((choice) => selectedIds.has(choice.id));
  const updates: ItemAiLookupFieldUpdate[] = [];
  const rejectedKeys = new Set<string>();

  choices.filter((choice): choice is ReviewFieldChoice => choice.kind === 'field').forEach((choice) => {
    if (selectedIds.has(choice.id)) {
      updates.push({ key: choice.fieldKey, value: fieldDraftValue(choice, draftValues) });
    } else {
      rejectedKeys.add(choice.proposalKey);
    }
  });

  const addStructuredDecision = <T extends ReviewChoice>(
    kind: T['kind'],
    proposalKey: string,
    buildValue: (selectedChoices: T[]) => unknown,
  ) => {
    const available = choices.filter((choice): choice is T => choice.kind === kind);
    if (!available.length) return;
    const accepted = available.filter((choice) => selectedIds.has(choice.id));
    if (!accepted.length) {
      rejectedKeys.add(proposalKey);
      return;
    }
    updates.push({ key: proposalKey, value: buildValue(accepted) });
  };

  addStructuredDecision<ReviewAttributeChoice>('attribute', 'attributes', (accepted) => mergeAttributes(item, accepted));
  addStructuredDecision<ReviewAccessoryChoice>('accessory', 'accessories', (accepted) => accepted.map((choice) => choice.proposed));
  addStructuredDecision<ReviewCaseChoice>('case', 'cases', (accepted) => accepted.map((choice) => choice.proposed));
  addStructuredDecision<ReviewDocumentChoice>('document', 'documentation', (accepted) => mergeDocumentation(item, accepted));
  addStructuredDecision<ReviewImageChoice>('image', 'product_image', (accepted) => accepted[0]?.proposed ?? null);

  const selectedManual = selected.find((choice): choice is ReviewDocumentChoice => (
    choice.kind === 'document' && choice.importAsManual
  ));
  if (selectedManual) {
    updates.push({ key: 'manual', value: selectedManual.proposed });
  } else if (choices.some((choice) => choice.kind === 'document' && choice.importAsManual)) {
    rejectedKeys.add('manual');
  }

  return {
    updates,
    rejectedKeys: Array.from(rejectedKeys),
  };
}

export function displayReviewValue(value: unknown): string {
  if (value == null || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const parts = value.map(displayReviewValue).filter((part) => part !== 'Not set');
    return parts.join(', ') || 'Not set';
  }
  if (isRecord(value)) {
    if (Array.isArray(value.values) && value.values.length) {
      const suffix = cleanText(value.unit) ? ` ${cleanText(value.unit)}` : '';
      return `${value.values.map(displayReviewValue).join(', ')}${suffix}`;
    }
    if (value.value != null && value.value !== '') {
      const suffix = cleanText(value.unit) ? ` ${cleanText(value.unit)}` : '';
      return `${displayReviewValue(value.value)}${suffix}`;
    }
    const parts = [
      cleanText(value.manufacturer),
      cleanText(value.model_number),
      Number(value.quantity) > 0 ? `Qty ${Number(value.quantity)}` : '',
      cleanText(value.kind),
      cleanText(value.notes),
    ].filter(Boolean);
    return parts.join(' · ') || JSON.stringify(value);
  }
  return cleanText(value) || 'Not set';
}
